import { useEffect, useState, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { isEmpty, isEqual, orderBy, cloneDeep, uniqBy } from 'lodash'
import moment from 'moment'

import { useGridApiRef } from '@mui/x-data-grid-premium'
import { makeStyles } from '@mui/styles'
import { Box } from '@mui/material'
import { IconThemeProvider } from 'custom-components/context/IconThemesContext'
import TileWrapper from '../../components/TileWrapper'
import { TileDatagrid, TOOLBAR_ITEMS } from '../../../dis-v2/_components/TileDatagrid'
import DriverDrivesSettingDialog from './DriverDrivesSettingDialog'

import tileApi from 'apis/disApi/tileApi'
import useDashboardQuery from '../../hooks/useDashboardQuery'
import { useTileDashboard } from '../../dashboard/Dashboard'
import { useDriverEmailQuery } from '../map/driver-map/hook/useDriverEmailQuery'
import { useDrives } from '../map/driver-map/hook/useDrives'

import { tileKeys } from '../../hooks/useTileQuery'
import { dateTimeFilterOperators } from '../../../../../components/data/dataHelpers'
import { isJson, logErrorMessage } from '../../../../../utils/functions/helpers'
import { getAutoUpdateFilters, generateRandomID, getLinkedTitle } from '../../helpers'
import { convertArrayToObject } from '../map/helper/functions'
import { checkByMasterDateTimeCondition } from 'utils/functions/helpers'
import {
	formatSpeed,
	formatSpeedToKilometersPerHour,
	formatDistance,
	getAddressFromGeocode,
} from 'utils/functions/mapUtils'
import { driverDrivesColumns } from './helper/constants'
import { driverDriveTileFieldIdMap } from 'utils/params/helpers'
import { useModalDashboardContext } from '../modal-dashboard/helper/modalDashboardContext'
import {
	getDefaultOperator,
	getChildLinkedFieldsByTileKey,
} from '../../../../../utils/functions/helpers'
import { getFilteredRows } from '../../../../data/dataHelpers'

const COLUMN_NAME = {
	FROM_DATE: 'fromDate',
	TO_DATE: 'toDate',
	USER_EMAIL: 'userEmail',
}

const useStyles = makeStyles(() => ({
	gridFooterActions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: '100%',
		width: '100%',
		paddingLeft: '16px',
		paddingRight: '16px',
	},
	cancelLoadingIcon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),

	dialog: {
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'center',
	},

	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
}))

export default function DriverDrivesTile(props) {
	const { tile } = props
	const { t } = useTranslation('common')
	const { environment } = useSelector((state) => state)
	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)
	const { id: dashboardKey } = useParams()
	const { passedIdValue: parentPassedIdValue, modalDashboardSetting: parentModalDashboardSetting } =
		useModalDashboardContext()

	const currentDashboardKey = useMemo(
		() => parentModalDashboardSetting?.dashboardKey ?? dashboardKey,
		[parentModalDashboardSetting, dashboardKey]
	)
	const { dashboardKeyList, updateMutation: updateDashboard } = useDashboardQuery({
		dashboardKey: currentDashboardKey,
	})

	const queryClient = useQueryClient()
	const updateTileMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(currentDashboardKey)),
	})

	const tileRef = useRef(null)
	const apiRef = useGridApiRef()

	const settings = useMemo(() => {
		if (tile?.settings && isJson(tile?.settings)) {
			return JSON.parse(tile?.settings ?? '{}')
		}

		return {}
	}, [tile?.settings])

	const { eventData, linkedFields, columnsChecked, otherOptionsChecked, firstLabel, secondLabel } =
		useMemo(() => settings, [settings])

	const [userEmail, setUserEmail] = useState('')
	const [fromDate, setFromDate] = useState('')
	const [toDate, setToDate] = useState('')

	const toolbarItems = useMemo(() => {
		const items = []
		const hasExport = otherOptionsChecked?.includes('enableExport')
		if (hasExport) {
			items.push(TOOLBAR_ITEMS.EXPORT)
		}
		return items
	}, [otherOptionsChecked])

	const displayedColumns = useMemo(() => {
		const fullDateTimeFormat = 'MM/DD/YYYY h:mm A'
		const isMetricUser = environment?.userCurrent?.units === 'metric'
		const defaultColumns = [
			{
				field: 'start_date_timestamp',
				headerName: 'Start date',
				title: 'Start date',
				type: 'dateTime',
				width: 170,
				filterOperators: dateTimeFilterOperators,
				valueGetter: (value) => {
					if (!value) {
						return ''
					}
					return moment.utc(value).utcOffset(environment.userCurrent.time.timezone.offset || 0)
				},
				valueFormatter: (value) => {
					if (!value) {
						return ''
					}
					return value.format(fullDateTimeFormat)
				},
			},
			{
				field: 'end_date_timestamp',
				headerName: 'Stop date',
				title: 'Stop date',
				type: 'dateTime',
				width: 170,
				filterOperators: dateTimeFilterOperators,
				valueGetter: (value) => {
					if (!value) {
						return ''
					}
					return moment.utc(value).utcOffset(environment.userCurrent.time.timezone.offset || 0)
				},
				valueFormatter: (value) => {
					if (!value) {
						return ''
					}
					return value.format(fullDateTimeFormat)
				},
			},
			{
				field: 'average_speed',
				headerName: 'Avg speed',
				title: 'Avg speed',
				type: 'string',
				width: 170,
				valueGetter: (value) => {
					if (!value) {
						return ''
					}
					return isMetricUser ? formatSpeedToKilometersPerHour(value) : formatSpeed(value)
				},
			},
			{
				field: 'external_id',
				headerName: 'Vehicle',
				title: 'Vehicle',
				type: 'string',
				width: 170,
			},
			{
				field: 'start_address',
				headerName: 'Start address',
				title: 'Start address',
				type: 'string',
				width: 170,
			},
			{
				field: 'stop_address',
				headerName: 'Stop address',
				title: 'Stop address',
				type: 'string',
				width: 170,
			},
			{
				field: 'distance_in_meters',
				headerName: 'Distance',
				title: 'Distance',
				type: 'string',
				width: 170,
				valueGetter: (value) => {
					if (!value) {
						return ''
					}
					return formatDistance(value, isMetricUser)
				},
			},
			{
				field: 'duration',
				headerName: 'Duration',
				title: 'Duration',
				type: 'string',
				width: 170,
			},
		]
		if (columnsChecked?.length > 0) {
			const filteredColumns = defaultColumns.filter((column) =>
				// WFM-423: support old and new column name
				columnsChecked.some(
					(columnChecked) =>
						columnChecked === column.field ||
						driverDriveTileFieldIdMap[columnChecked] === column.field
				)
			)

			return filteredColumns
		}
		return []
	}, [columnsChecked, environment])

	const [settingsOpen, setSettingsOpen] = useState(false)

	useEffect(() => {
		if (isEqual(userEmail, eventData?.userEmail)) return
		setUserEmail(eventData?.userEmail)
	}, [eventData?.userEmail])

	useEffect(() => {
		setFromDate(moment.utc(eventData?.fromDate).unix())
	}, [eventData?.fromDate])

	useEffect(() => {
		// If To date is empty, use now
		if (!eventData?.toDate) {
			setToDate(moment.utc().unix())
			return
		}
		setToDate(moment.utc(eventData?.toDate).unix())
	}, [eventData?.toDate])

	const { selectedFields } = useTileDashboard()

	const { filterConfigs, hasPrimaryKey, conditions } = useMemo(
		() => getAutoUpdateFilters(settings?.linkedFields, selectedFields, dashboardKeyList),
		[settings?.linkedFields, selectedFields, dashboardKeyList]
	)

	// Remove id because it is random property and make app re-render many times
	const filterWithoutId = useMemo(
		() =>
			filterConfigs.map((item) => {
				const { id, ...rest } = item
				return rest
			}),
		[filterConfigs]
	)

	const conditionsWithoutId = useMemo(() => {
		const result = {}
		for (const key in conditions) {
			const value = conditions[key]
			const { id, ...rest } = value
			result[key] = rest
		}
		return result
	}, [conditions])

	useEffect(() => {
		if (hasPrimaryKey) {
			const newSettings = convertArrayToObject(filterWithoutId)

			const shouldSetEmail = Object.prototype.hasOwnProperty.call(newSettings, 'userEmail')
			const shouldSetFromDate = Object.prototype.hasOwnProperty.call(newSettings, 'fromDate')
			const shouldSetToDate = Object.prototype.hasOwnProperty.call(newSettings, 'toDate')

			const valueToCheck = {
				userEmail: shouldSetEmail ? newSettings.userEmail : eventData?.userEmail,
				fromDate: shouldSetFromDate ? newSettings.fromDate : eventData?.fromDate,
				toDate: shouldSetToDate ? newSettings.toDate : eventData?.toDate,
			}

			if (!isEmpty(conditionsWithoutId)) {
				Object.values(COLUMN_NAME).forEach((key) => {
					const condition = conditionsWithoutId[key]
					if (isEmpty(condition)) return

					const dateValue = valueToCheck[key]
					const isValid = checkByMasterDateTimeCondition(
						dateValue,
						condition,
						condition?.keyData === 'Master_DateTime'
					)
					if (!isValid) {
						valueToCheck[key] = ''
					}
				})
			}

			if (!isEqual(valueToCheck.userEmail, userEmail)) {
				setUserEmail(valueToCheck.userEmail)
			}

			let startUnix = moment.utc(valueToCheck.fromDate).unix()
			if (!isEqual(startUnix, fromDate)) {
				setFromDate(startUnix)
			}

			let toUnix = moment.utc(valueToCheck.toDate).unix()
			if (!isEqual(toUnix, toDate)) {
				setToDate(toUnix)
			}
		} else {
			setUserEmail(eventData?.userEmail)
			setFromDate(moment.utc(eventData?.fromDate).unix())
			setToDate(moment.utc(eventData?.toDate).unix())
		}
	}, [JSON.stringify(filterWithoutId), JSON.stringify(conditionsWithoutId), hasPrimaryKey])

	const { driverInfoByEmail, isLoadingUser } = useDriverEmailQuery({
		userEmail,
	})

	const userId = useMemo(() => {
		if (isLoadingUser || !userEmail || isEmpty(driverInfoByEmail)) return null

		if (!isEqual(userEmail, driverInfoByEmail.email)) return null

		return driverInfoByEmail.id
	}, [userEmail, isLoadingUser, driverInfoByEmail])

	// Fetch drives
	const { allDrives, isLoadingDrives: isLoadingAllDrives } = useDrives({
		userId: userId,
		fromDate,
		toDate,
		showHistory: true,
	})

	const [formattedRows, setFormattedRows] = useState([])
	const [isLoading, setIsLoading] = useState(false)

	function calcDuration(startDate, endDate) {
		if (!startDate || !endDate) return ''

		const start = moment.utc(startDate)
		const end = moment.utc(endDate)

		// Guard: if end is before start
		if (!end.isAfter(start)) return ''

		const duration = moment.duration(end.diff(start))
		const hours = Math.floor(duration.asHours())
		const minutes = Math.floor(duration.asMinutes()) % 60

		const durationParts = []
		if (hours > 0) durationParts.push(`${hours} hr`)
		if (minutes > 0) durationParts.push(`${minutes} min`)

		return durationParts.join(' ')
	}

	const parentFilters = useMemo(() => {
		if (isEmpty(parentModalDashboardSetting)) return []

		const linkedFields = getChildLinkedFieldsByTileKey(tile?.key, parentModalDashboardSetting) || []

		const childLinkedFilters = linkedFields
			?.map((name, index) => {
				const column = driverDrivesColumns.find((col) => col.name === name)
				if (!column) return null

				const defaultOperator = getDefaultOperator(column.type, true)
				return {
					field: name,
					operator: parentPassedIdValue === '' ? 'isEmpty' : defaultOperator,
					value: parentPassedIdValue,
					type: column.type,
				}
			})
			.filter((item) => !!item)

		return childLinkedFilters
	}, [parentPassedIdValue, parentModalDashboardSetting, tile?.key])

	useEffect(() => {
		if (isLoadingAllDrives) return

		const formatRows = async () => {
			try {
				if (isEmpty(allDrives)) {
					setIsLoading(false)
					if (!isEqual(formattedRows, [])) {
						setFormattedRows([])
					}
					return
				}

				const rows = await Promise.all(
					allDrives.map(async (drive) => {
						const { start_lat, start_long, end_lat, end_long, ...rest } = drive

						let start_address = ''
						let stop_address = ''

						try {
							start_address = await getAddressFromGeocode(start_lat, start_long)
						} catch (e) {
							console.error('Error fetching start address', e)
							console.log(`start_locations: { lat: ${end_lat}, long: ${end_long}`)
						}

						try {
							stop_address = await getAddressFromGeocode(end_lat, end_long)
						} catch (e) {
							console.error('Error fetching stop address', e)
							console.log(`end_locations: { lat: ${end_lat}, long: ${end_long}}`)
						}

						return {
							...rest,
							start_address,
							stop_address,
							external_id: drive.vehicle?.external_id || '',
							duration: calcDuration(drive.start_date_timestamp, drive.end_date_timestamp),
							driveId: drive.id,
						}
					})
				)

				if (!isEqual(formattedRows, rows)) {
					if (isEmpty(parentFilters)) {
						setFormattedRows(rows)
					} else {
						const filterdRows = getFilteredRows(parentFilters, rows)
						setFormattedRows(filterdRows)
					}
				}
				setIsLoading(false)
			} catch (error) {
				console.error('Error formatting rows:', error)
				setIsLoading(false)
			}
		}

		setIsLoading(true)
		formatRows()
	}, [allDrives, columnsChecked, isLoadingAllDrives, parentFilters])

	const { firstLabelValue, secondLabelValue } = useMemo(
		() => getLinkedTitle(selectedFields, dashboardKeyList, firstLabel, secondLabel, environment, t),
		[selectedFields, firstLabel, secondLabel, environment, t]
	)

	const titleSuffix = useMemo(() => {
		if (!firstLabelValue && !secondLabelValue) {
			return ''
		}

		const suffix = ` - ${firstLabelValue} ${secondLabelValue}`
		return suffix
	}, [selectedFields, firstLabelValue, secondLabelValue])

	const displayTitle = useMemo(() => {
		return isEmpty(titleSuffix) ? tile?.i : `${tile?.i} ${titleSuffix}`
	}, [titleSuffix, tile?.i])

	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const handleSubmitSetting = async (data) => {
		try {
			const {
				eventData,
				linkedFields,
				columnsChecked,
				otherOptionsChecked,
				primaryKeyList,
				firstLabel,
				secondLabel,
			} = data
			const currentDashboardKeyList = cloneDeep(dashboardKeyList)
			const tileName = tile.i
			const tileKey = tile.key
			const otherDashboardKeys = currentDashboardKeyList.filter(
				(item) => item.tileKey !== tileKey && item.tileName !== tileName
			)
			const primaryKeysToSave = [...otherDashboardKeys]

			if (!isEmpty(primaryKeyList)) {
				const newPrimaryKeyList = [
					...primaryKeyList.map((item) => {
						const newPrimaryKey = {
							id: generateRandomID(),
							key: item,
							tileName,
							tileKey,
						}
						const columnByKey = driverDrivesColumns?.find((column) => column.name === item)
						if (columnByKey) {
							newPrimaryKey['columnType'] = columnByKey.type
						}

						newPrimaryKey['driverDriveKey'] = driverDriveTileFieldIdMap[item]

						return newPrimaryKey
					}),
				]
				const uniqNewPrimaryKeyList = uniqBy(
					newPrimaryKeyList,
					(item) => `${item.key}-${tile.tileName}-${tile.tileKey}`
				)

				// mark delete primary key
				const deletedPrimaryKeyList = currentDashboardKeyList
					.filter((item) => tileName === item.tileName && tileKey === item.tileKey)
					.map((oldItem) => {
						const hasInNewList = uniqNewPrimaryKeyList.find(
							(newItem) =>
								oldItem.key === newItem.key &&
								oldItem.tileName === newItem.tileName &&
								oldItem.tileKey === newItem.tileKey
						)

						if (hasInNewList) return null

						return {
							...oldItem,
							deleted: true,
						}
					})
					.filter((item) => !!item)

				const allTileKeys = [...uniqNewPrimaryKeyList, ...deletedPrimaryKeyList]

				const keepOldPrimaryKeyList = allTileKeys
					.map((item) => {
						const deletedKey = deletedPrimaryKeyList.find(
							(deletedItem) =>
								deletedItem.key === item.key &&
								deletedItem.tileName === item.tileName &&
								deletedItem.tileKey === item.tileKey
						)
						if (Boolean(deletedKey)) {
							return null
						}

						const oldDashboardKeyItem = currentDashboardKeyList.find(
							(oldItem) =>
								oldItem.key === item.key &&
								oldItem.tileName === item.tileName &&
								oldItem.tileKey === item.tileKey
						)
						if (Boolean(oldDashboardKeyItem)) {
							const columnByKey = driverDrivesColumns?.find(
								(column) => column.name === oldDashboardKeyItem.key
							)
							if (columnByKey) {
								oldDashboardKeyItem['columnType'] = columnByKey.type
							}
							return { ...oldDashboardKeyItem, old: true }
						}

						const columnByKey = driverDrivesColumns?.find((column) => column.name === item.key)
						if (columnByKey) {
							item['columnType'] = columnByKey.type
						}

						return item
					})
					.filter((item) => !!item)

				primaryKeysToSave.push(...keepOldPrimaryKeyList)
			}

			const orderedSaveList = orderBy(
				primaryKeysToSave,
				(item) => `${item.tileName}-${item.tileKey}-${item.id}`
			)

			await Promise.all([
				updateDashboard.mutateAsync({
					key: currentDashboardKey,
					data: {
						variables: JSON.stringify(orderedSaveList),
					},
					token: environment.apiToken,
				}),
				updateTileMutation.mutateAsync({
					dashboardKey: currentDashboardKey,
					tileKey: tile.key,
					data: {
						settings: JSON.stringify({
							...settings,
							eventData,
							linkedFields,
							columnsChecked,
							otherOptionsChecked,
							firstLabel,
							secondLabel,
						}),
					},
					token: environment.apiToken,
				}),
			])
		} catch (error) {
			logErrorMessage(error)
		} finally {
			handleCloseDialog()
		}
	}

	const handleResizeTileWidth = async (width) => {
		try {
			const editedSettings = JSON.stringify({
				...settings,
				tileWidth: width,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey: currentDashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	return (
		<IconThemeProvider values={iconTheme}>
			<TileWrapper
				title={displayTitle}
				onSettingClick={handleOpenDialog}
				ref={tileRef}
				isExpandDialogBtn
			>
				<DriverDrivesSettingDialog
					tileElementWidth={tileRef?.current?.clientWidth}
					defaultTileWidth={300}
					tile={tile}
					eventData={eventData}
					linkedFields={linkedFields}
					columnsChecked={columnsChecked}
					otherOptionsChecked={otherOptionsChecked}
					isSubmitting={updateTileMutation.isLoading}
					open={settingsOpen}
					onClose={handleCloseDialog}
					onSubmit={handleSubmitSetting}
					onResizeTileWidth={handleResizeTileWidth}
					dashboardKeyList={dashboardKeyList}
					firstLabel={firstLabel}
					secondLabel={secondLabel}
				/>
				<Box
					sx={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: 30,
						bottom: 0,
						background: '#fff',

						'& .MuiDataGrid-selectedRowCount': {
							opacity: '0 !important',
						},
					}}
				>
					<TileDatagrid
						tile={tile}
						columns={displayedColumns}
						rows={formattedRows}
						isLoading={isLoadingUser || isLoadingAllDrives || isLoading}
						toolbarItems={toolbarItems}
						apiRef={apiRef}
						printTitle={tile?.i}
					/>
				</Box>
			</TileWrapper>
		</IconThemeProvider>
	)
}
